import type { PaperType } from "@markeroo/shared";
import { ui } from "@/strings";

interface PaperTypeOption {
  value: PaperType;
  label: string;
  description: string;
}

const PAPER_TYPE_OPTIONS: PaperTypeOption[] = [
  {
    value: "opinion_argument",
    label: ui.labels.paperTypeOpinionArgument,
    description: ui.labels.paperTypeOpinionArgumentDescription,
  },
  {
    value: "informative_explanatory",
    label: ui.labels.paperTypeInformativeExplanatory,
    description: ui.labels.paperTypeInformativeExplanatoryDescription,
  },
  {
    value: "narrative",
    label: ui.labels.paperTypeNarrative,
    description: ui.labels.paperTypeNarrativeDescription,
  },
  {
    value: "other",
    label: ui.labels.paperTypeOther,
    description: ui.labels.paperTypeOtherDescription,
  },
];

interface PaperTypeSelectorProps {
  value: PaperType | null;
  onChange: (value: PaperType) => void;
  showError?: boolean;
}

export function PaperTypeSelector({
  value,
  onChange,
  showError = false,
}: PaperTypeSelectorProps) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-text-theme-primary">
          {ui.labels.paperTypeSelectorTitle}
        </h3>
        <p className="mt-1 text-sm text-text-theme-muted">
          {ui.labels.paperTypeSelectorSubtitle}
        </p>
      </div>

      {showError && !value && (
        <p className="text-sm font-medium text-semantic-error">
          {ui.labels.paperTypeRequired}
        </p>
      )}

      <div className="flex flex-col gap-3">
        {PAPER_TYPE_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          return (
            <label
              key={option.value}
              className={`relative flex cursor-pointer rounded-xl border-2 p-4 transition-all ${
                isSelected
                  ? "border-primary bg-brand-50 ring-1 ring-primary"
                  : "border-border bg-white hover:border-border-hover hover:bg-surface-secondary"
              }`}
            >
              <input
                type="radio"
                name="paperType"
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    isSelected
                      ? "border-primary bg-primary"
                      : "border-border bg-white"
                  }`}
                >
                  {isSelected && (
                    <div className="h-2 w-2 rounded-full bg-white" />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span
                    className={`text-sm font-semibold ${
                      isSelected ? "text-brand-900" : "text-text-theme-primary"
                    }`}
                  >
                    {option.label}
                  </span>
                  <span
                    className={`text-sm ${
                      isSelected ? "text-brand-700" : "text-text-theme-muted"
                    }`}
                  >
                    {option.description}
                  </span>
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
